<template>
  <div class="grid-x flex-grow settings-admin-form-mobile">
    <Loading class="flex-grow" :loading="waitPending">
      <div v-if="admin" class="cell auto cell-block-y flex flex-col flex-grow">
        <div class="flex flex-col divide-y bg-hint">
          <div class="flex flex-row items-center justify-between py-6 px-8">
            <div>
              <b class="text-18px leading-22px">{{ adminDisplayName }}</b>
              <div class="text-12px text-muted mt-1">{{ admin.login }} · {{ roleLabel(admin.role) }}</div>
            </div>
          </div>

          <div v-if="!accessV2Ready" class="px-8 py-4 bg-warning">
            Backend Admin Access V2 ещё не активен. Изменение роли и прав временно заблокировано.
          </div>

          <div class="px-8 py-6 grid-x grid-margin-x">
            <div class="cell medium-6 flex flex-col space-y-3">
              <input class="input" v-model="profileForm.name" placeholder="Имя">
              <input class="input" v-model="profileForm.phone" placeholder="Телефон">
              <input class="input" v-model="profileForm.email" placeholder="Email">
            </div>
            <div class="cell medium-6 flex flex-col space-y-3">
              <input class="input" v-model="profileForm.country" placeholder="Страна">
              <input class="input" v-model="profileForm.region" placeholder="Регион">
              <input class="input" v-model="profileForm.city" placeholder="Город">
            </div>
          </div>

          <div class="flex flex-row justify-end px-8 py-4">
            <button class="btn btn-sm btn-primary" :disabled="isSavingProfile || !canManageTarget" @click="saveProfile">
              {{ isSavingProfile ? 'Сохранение...' : 'Сохранить профиль' }}
            </button>
          </div>
        </div>

        <div class="flex flex-col divide-y">
          <div class="flex flex-row items-center space-x-8 px-8 py-6">
            <div class="w-64"><b class="text-14px leading-16px">Статус</b></div>
            <Check label="Активен" :value="admin.active" :disabled="admin.isCurrentUser || !canManageTarget" @input="toggleActive" />
          </div>

          <div class="flex flex-row items-center space-x-8 px-8 py-6">
            <div class="w-64">
              <b class="text-14px leading-16px">Тип аккаунта</b>
              <div class="text-12px text-muted mt-1">Суперадминистратор всегда имеет полный доступ.</div>
            </div>
            <select
              class="input w-64"
              v-model="accessForm.role"
              :disabled="!canEditAccess || !currentIsSuperAdmin"
            >
              <option value="admin">Администратор</option>
              <option value="super_admin">Суперадминистратор</option>
            </select>
          </div>

          <div
            v-for="item in visiblePermissions"
            :key="item.key"
            class="flex flex-row items-center space-x-8 px-8 py-5"
          >
            <div class="w-64">
              <b class="text-14px leading-16px">{{ item.label }}</b>
              <div class="text-12px text-muted mt-1">{{ item.description }}</div>
            </div>
            <Check
              label="Разрешено"
              :value="accessForm.accessPermissions.includes(item.key)"
              :disabled="!canEditAccess"
              @input="(value) => togglePermission(item.key, value)"
            />
          </div>

          <div class="px-8 py-5 bg-hint">
            <b class="text-14px leading-16px">Коммуникации в карточках</b>
            <div class="text-12px text-muted mt-1">
              Право «Коммуникации» управляет только общей очередью.
              Вкладки «Коммуникации» внутри карточек заказчика и исполнителя по-прежнему
              определяются правами «Заказчики» и «Исполнители».
            </div>
          </div>

          <div class="flex flex-row justify-between items-center px-8 py-6">
            <div class="text-12px text-muted">
              <span v-if="accessForm.role === 'super_admin'">
                Полный доступ включён ролью; отмеченные права — сохранённый набор на случай понижения.
              </span>
              <span v-else>Изменение прав завершит активную сессию этого администратора.</span>
            </div>
            <button
              class="btn btn-primary"
              :disabled="isSavingAccess || !canEditAccess"
              @click="saveAccess"
            >
              {{ isSavingAccess ? 'Сохранение...' : 'Сохранить роль и права' }}
            </button>
          </div>
        </div>

        <div class="flex flex-row items-center justify-end space-x-3 px-8 py-6 bg-hint">
          <button class="btn btn-sm btn-primary" :disabled="isResetting || !canManageTarget" @click="resetCredentials">
            Сброс данных авторизации
          </button>
          <button class="btn btn-sm btn-danger" :disabled="isDeleting || admin.isCurrentUser || !canManageTarget" @click="removeAdmin">
            Удалить администратора
          </button>
        </div>
      </div>
    </Loading>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import Check from '@/components/Check.vue';
import Loading from '@/components/Loading.vue';
import {
  setAdminActive,
  resetAdminSecureCredentials,
  deleteAdmin,
  updateAdminProfile,
  updateAdminAccess,
} from '@/api/settings';
import {
  ADMIN_PERMISSIONS,
  canAccess,
  hasAccessV2,
  isSuperAdmin,
  legacyPermissionsFromAccess,
} from '@/access/adminAccess';

const PERMISSION_ITEMS = [
  { key: ADMIN_PERMISSIONS.ORDERS, label: 'Заказы', description: 'Просмотр и работа с заказами.' },
  { key: ADMIN_PERMISSIONS.CONTRACTORS, label: 'Исполнители', description: 'Карточки, операции и вкладка «Коммуникации» в карточке.' },
  { key: ADMIN_PERMISSIONS.COMMUNICATIONS, label: 'Коммуникации', description: 'Общая очередь «Коммуникации» и внешние Talk-Me диалоги.' },
  { key: ADMIN_PERMISSIONS.CUSTOMERS, label: 'Заказчики', description: 'Карточки и вкладка «Коммуникации» в карточке.' },
  { key: ADMIN_PERMISSIONS.SUBJECTS, label: 'Объекты', description: 'Места захоронения и связанные операции.' },
  { key: ADMIN_PERMISSIONS.SERVICES, label: 'Услуги', description: 'Категории, услуги, опции и сортировка.' },
  { key: ADMIN_PERMISSIONS.PARTNERS, label: 'Реферралы', description: 'Партнёры и партнёрские страницы.' },
  { key: ADMIN_PERMISSIONS.SETTINGS, label: 'Настройки', description: 'Системные, юридические, SMS и региональные настройки.' },
  {
    key: ADMIN_PERMISSIONS.ADMINS,
    label: 'Администраторы',
    description: 'Создание и управление обычными администраторами.',
  },
  { key: ADMIN_PERMISSIONS.ANALYTICS, label: 'Аналитика', description: 'Business Analytics: продажи, клиенты, retention, регионы и операционные KPI.' },
  { key: ADMIN_PERMISSIONS.MONITORING, label: 'Мониторинг', description: 'System Health и инциденты.' },
  { key: ADMIN_PERMISSIONS.DATABASE, label: 'База данных', description: 'Экспорт заказчиков и исполнителей.' },
];

export default {
  components: { Check, Loading },
  data() {
    return {
      isSavingProfile: false,
      isSavingAccess: false,
      isResetting: false,
      isDeleting: false,
      profileForm: {
        name: '',
        phone: '',
        email: '',
        country: '',
        region: '',
        city: '',
      },
      accessForm: { role: 'admin', accessPermissions: [] },
    };
  },
  computed: {
    ...mapState({
      admins: (state) => state.settings.admins,
      currentUser: (state) => state.auth.user,
    }),
    id() { return _.get(this.$route, 'params.id'); },
    admin() {
      return _.find(this.admins, (obj) => parseInt(obj.id, 10) === parseInt(this.id, 10)) || null;
    },
    adminDisplayName() {
      return _.get(this.admin, 'name') || _.get(this.admin, 'login') || `Администратор #${this.id}`;
    },
    waitPending() { return this.$wait.is('fetch.admins'); },
    accessV2Ready() { return hasAccessV2(this.currentUser) && hasAccessV2(this.admin); },
    currentIsSuperAdmin() { return isSuperAdmin(this.currentUser); },
    canManageTarget() {
      if (!this.accessV2Ready) return false;
      if (this.currentIsSuperAdmin) return true;
      if (isSuperAdmin(this.admin)) return false;
      const targetPermissions = Array.isArray(this.admin.accessPermissions)
        ? this.admin.accessPermissions
        : [];
      return targetPermissions.every((permission) => canAccess(this.currentUser, permission));
    },
    canEditAccess() {
      return this.canManageTarget;
    },
    visiblePermissions() {
      if (this.currentIsSuperAdmin) return PERMISSION_ITEMS;
      return PERMISSION_ITEMS.filter((item) => canAccess(this.currentUser, item.key));
    },
  },
  watch: {
    admin: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.profileForm = {
          name: value.name || '',
          phone: value.phone || '',
          email: value.email || '',
          country: value.country || '',
          region: value.region || '',
          city: value.city || '',
        };
        this.accessForm = {
          role: value.role || 'admin',
          accessPermissions: Array.isArray(value.accessPermissions) ? [...value.accessPermissions] : [],
        };
      },
    },
  },
  methods: {
    ...mapActions({ fetch: 'settings/ACT_FETCH_ADMINS', logout: 'auth/ACT_LOGOUT' }),
    roleLabel(role) { return role === 'super_admin' ? 'Суперадминистратор' : 'Администратор'; },
    togglePermission(permission, value) {
      const set = new Set(this.accessForm.accessPermissions);
      if (value) set.add(permission); else set.delete(permission);
      this.accessForm.accessPermissions = Array.from(set);
    },
    async saveProfile() {
      if (!this.admin || this.isSavingProfile) return;
      if (!this.profileForm.name || !this.profileForm.phone || !this.profileForm.email
        || !this.profileForm.country || !this.profileForm.region || !this.profileForm.city) {
        if (this.$toast && this.$toast.error) this.$toast.error('Заполните все поля профиля');
        return;
      }
      try {
        this.isSavingProfile = true;
        await updateAdminProfile(this.id, { ...this.profileForm });
        await this.fetch();
        if (this.$toast && this.$toast.success) this.$toast.success('Профиль администратора обновлён');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error('Ошибка обновления профиля администратора');
      } finally { this.isSavingProfile = false; }
    },
    async saveAccess() {
      if (!this.canEditAccess || this.isSavingAccess) return;
      if (this.accessForm.role === 'admin' && this.admin.active
        && this.accessForm.accessPermissions.length === 0) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Активному администратору нужно назначить хотя бы одно право доступа');
        }
        return;
      }
      try {
        this.isSavingAccess = true;
        const accessPermissions = [...this.accessForm.accessPermissions];
        const response = await updateAdminAccess(this.id, {
          role: this.accessForm.role,
          accessPermissions,
          permissions: legacyPermissionsFromAccess(accessPermissions),
        });
        if (_.get(response, 'data.currentSessionInvalidated')) {
          await this.logout();
          return;
        }
        await this.fetch();
        if (this.$toast && this.$toast.success) this.$toast.success('Роль и права сохранены');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error('Ошибка изменения роли или прав');
      } finally { this.isSavingAccess = false; }
    },
    async toggleActive(value) {
      if (this.admin.isCurrentUser && value === false) return;
      if (value && this.accessForm.role === 'admin'
        && this.accessForm.accessPermissions.length === 0) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Перед активацией назначьте администратору хотя бы одно право доступа');
        }
        return;
      }
      try {
        await setAdminActive(this.id, value);
        await this.fetch();
        if (this.$toast && this.$toast.success) this.$toast.success(value ? 'Администратор активирован' : 'Администратор отключён');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error('Ошибка изменения статуса администратора');
      }
    },
    async resetCredentials() {
      if (!this.admin || this.isResetting) return;
      if (!window.confirm(`Сбросить данные авторизации для администратора "${this.adminDisplayName}"?`)) return;
      try {
        this.isResetting = true;
        await resetAdminSecureCredentials(this.id);
        if (this.admin.isCurrentUser) {
          await this.logout();
          return;
        }
        await this.fetch();
        if (this.$toast && this.$toast.success) this.$toast.success('Данные авторизации сброшены');
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error('Ошибка сброса данных авторизации');
      } finally { this.isResetting = false; }
    },
    async removeAdmin() {
      if (!this.admin || this.isDeleting || this.admin.isCurrentUser) return;
      if (!window.confirm(`Удалить администратора "${this.adminDisplayName}"?`)) return;
      try {
        this.isDeleting = true;
        await deleteAdmin(this.id);
        await this.fetch();
        this.$router.push({ name: 'settings.admins' });
      } catch (e) {
        if (this.$toast && this.$toast.error) this.$toast.error('Ошибка удаления администратора');
      } finally { this.isDeleting = false; }
    },
  },
  mounted() {
    if (!this.admins.length) this.fetch();
  },
};
</script>
